/**
 * Lazy Loader Utility for Videos
 * Handles intelligent loading of video content based on visibility and user interaction
 */

export class LazyVideoLoader {
  constructor() {
    this.videos = [];
    this.observer = null;
    this.init();
  }

  /**
   * Initialize the lazy loader
   */
  init() {
    // Find all videos with preload="none"
    this.videos = Array.from(document.querySelectorAll('video[preload="none"]'));

    if (this.videos.length === 0) {
      return;
    }

    // Use Intersection Observer to detect when videos are near viewport
    this.setupIntersectionObserver();

    // Listen to accordion changes to load videos when their tab becomes active
    this.setupAccordionListeners();
  }

  /**
   * Set up Intersection Observer for viewport-based loading
   */
  setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: '200px', // Start loading 200px before video enters viewport
      threshold: 0.01
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.loadVideo(entry.target);
        }
      });
    }, options);

    // Observe all lazy videos
    this.videos.forEach(video => {
      this.observer.observe(video);
    });
  }

  /**
   * Set up listeners for accordion interactions
   */
  setupAccordionListeners() {
    const accordionItems = document.querySelectorAll('.accordion-item');

    accordionItems.forEach(item => {
      item.addEventListener('click', () => {
        const accordionId = item.getAttribute('data-accordion');

        // Find the video associated with this accordion item
        const video = document.querySelector(`video[data-image="${accordionId}"]`);

        if (video && video.getAttribute('preload') === 'none') {
          this.loadVideo(video);
        }
      });
    });
  }

  /**
   * Load a video by changing preload attribute and loading it
   * @param {HTMLVideoElement} video - The video element to load
   */
  loadVideo(video) {
    if (!video || video.getAttribute('data-loaded') === 'true') {
      return;
    }

    // Change preload to auto to start loading
    video.setAttribute('preload', 'auto');

    // Load the video
    video.load();

    // Mark as loaded to prevent duplicate loading
    video.setAttribute('data-loaded', 'true');

    // Stop observing this video
    if (this.observer) {
      this.observer.unobserve(video);
    }

    console.log(`Lazy loaded video: ${video.src}`);
  }

  /**
   * Manually load a specific video by selector
   * @param {string} selector - CSS selector for the video
   */
  loadVideoBySelector(selector) {
    const video = document.querySelector(selector);
    if (video) {
      this.loadVideo(video);
    }
  }

  /**
   * Load all remaining videos (useful for "load all" scenarios)
   */
  loadAllVideos() {
    this.videos.forEach(video => {
      this.loadVideo(video);
    });
  }

  /**
   * Clean up and disconnect observer
   */
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    this.videos = [];
  }
}

/**
 * Initialize lazy video loader
 * @returns {LazyVideoLoader} Instance of the lazy video loader
 */
export function initLazyVideoLoader() {
  return new LazyVideoLoader();
}
